import { useEffect, useState } from "react";
import type { SpendRequest, SpendTask } from "../../../src/admin/spend.ts";
import { fetchJson } from "../lib/fetchJson.ts";
import { formatSpend, formatTokens } from "../lib/spend.ts";
import { day, Refusal } from "./bits.tsx";
import { percent, requestName, totalTokens, UsageCells, UsageHeads } from "./spend-bits.tsx";

// One Request's spend over its whole life, opened from the Spend screen's
// top Requests: its Tasks in the order they first reported, each with its
// Runs, and its refinement and pull request description rows beside them.
// Tasks have no title on this screen, so they are numbered; a deleted
// Request reads "Deleted Request". See ADR-0051 §6.

// The span from a Run's first report to its last, as `12m 30s`.
function span(firstAt: string, lastAt: string): string {
  const s = Math.max(0, Math.round((Date.parse(lastAt) - Date.parse(firstAt)) / 1000));
  if (Number.isNaN(s)) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

// Each Task's name in the order the ledger lists them (by first report):
// "Task 1", "Task 2", …; the Runs outside any Task are named as such.
function taskNames(tasks: SpendTask[]): string[] {
  let n = 0;
  return tasks.map((t) => (t.taskId === null ? "Outside a Task" : `Task ${++n}`));
}

/**
 * A Request's spend page: totals, the Tasks and Runs table and the
 * refinement and pull request description rows, read from
 * `/api/spend/requests/:requestId`. Rendered by the admin shell at
 * `/spend/requests/:requestId`.
 */
export function SpendRequestPage({ requestId }: { requestId: string }): JSX.Element {
  const [request, setRequest] = useState<SpendRequest | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchJson<{ request: SpendRequest }>(`/api/spend/requests/${encodeURIComponent(requestId)}`)
      .then((b) => live && setRequest(b.request))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [requestId]);

  const back = (
    <a className="back-link" href="/spend">
      ← Back to Spend
    </a>
  );

  if (error) {
    return (
      <div className="spend-page">
        {back}
        <Refusal message={error} />
      </div>
    );
  }
  if (!request) {
    return (
      <div className="spend-page">
        {back}
        <p aria-live="polite">Loading…</p>
      </div>
    );
  }

  const t = request.totals;
  const names = taskNames(request.tasks);
  return (
    <div className="spend-page">
      {back}
      <div className="spend-request-head">
        <h2>{requestName(request.requestTitle)}</h2>
        <span className="meta">
          {request.organisationName ?? "—"} · {request.projectName ?? "—"}
        </span>
      </div>

      <div className="stats spend-stats">
        <div className="stat" data-testid="request-cost">
          <div className="k">Cost</div>
          <div className="v">{formatSpend(t.costMicros)}</div>
          {t.unpricedCalls > 0 ? <div className="sub">{t.unpricedCalls} unpriced, not counted</div> : null}
        </div>
        <div className="stat">
          <div className="k">Tokens</div>
          <div className="v">{formatTokens(totalTokens(t))}</div>
          <div className="sub">
            in {formatTokens(t.inputTokens)} · out {formatTokens(t.outputTokens)}
          </div>
        </div>
        <div className="stat">
          <div className="k">Calls</div>
          <div className="v">{t.calls}</div>
        </div>
        <div className="stat">
          <div className="k">Cache hit</div>
          <div className="v">{percent(t.cacheHitRatio)}</div>
        </div>
      </div>

      <section className="panel" aria-label="Tasks and Runs">
        <div className="panel-head">
          <h2>Tasks and Runs</h2>
          <span className="meta">in the order each first reported; retries included</span>
        </div>
        <div className="panel-body flush scroll-x">
          {request.tasks.length === 0 ? (
            <p className="empty">No Run of this Request reported usage.</p>
          ) : (
            <table className="grid spend">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Kind</th>
                  <th>Attempt</th>
                  <th>Stage</th>
                  <th>Model</th>
                  <th className="num">Reported over</th>
                  <UsageHeads />
                </tr>
              </thead>
              <tbody>
                {request.tasks.map((task, i) => (
                  <TaskRows key={task.taskId ?? "none"} task={task} name={names[i] ?? ""} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="panel" aria-label="Refinement and pull request description">
        <div className="panel-head">
          <h2>Refinement and pull request description</h2>
          <span className="meta">spend outside any Run</span>
        </div>
        <div className="panel-body flush scroll-x">
          {request.conversations.length === 0 ? (
            <p className="empty">No refinement or pull request description spend.</p>
          ) : (
            <table className="grid spend">
              <thead>
                <tr>
                  <th>Stage</th>
                  <th>Runtime</th>
                  <th>Model</th>
                  <th>Days</th>
                  <UsageHeads />
                </tr>
              </thead>
              <tbody>
                {request.conversations.map((c) => (
                  <tr key={`${c.stage} ${c.runtime} ${c.provider ?? ""} ${c.model}`}>
                    <td>{c.stage}</td>
                    <td>{c.runtime}</td>
                    <td className="mono">
                      {c.model}
                      {c.provider ? <span className="sub">{c.provider}</span> : null}
                    </td>
                    <td className="nowrap">
                      {day(c.firstAt) === day(c.lastAt) ? day(c.firstAt) : `${day(c.firstAt)} → ${day(c.lastAt)}`}
                    </td>
                    <UsageCells u={c} />
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

function TaskRows({ task, name }: { task: SpendTask; name: string }): JSX.Element {
  return (
    <>
      <tr className="task-head">
        <td colSpan={6} data-testid="task-head">
          {name}
          <span className="sub">
            {task.runs.length} {task.runs.length === 1 ? "Run" : "Runs"}
          </span>
        </td>
        <UsageCells u={task} />
      </tr>
      {task.runs.map((r) => (
        <tr key={`${r.runId} ${r.stage} ${r.model}`}>
          <td className="mono" title={r.runId}>
            {r.runId.length > 12 ? `${r.runId.slice(0, 12)}…` : r.runId}
          </td>
          <td>{r.runKind ?? "—"}</td>
          <td>{r.attemptNo === null ? "—" : `#${r.attemptNo}`}</td>
          <td>{r.stage}</td>
          <td className="mono">
            {r.model}
            <span className="sub">
              {r.runtime}
              {r.provider ? ` · ${r.provider}` : ""}
            </span>
          </td>
          <td className="num">{span(r.firstAt, r.lastAt)}</td>
          <UsageCells u={r} />
        </tr>
      ))}
    </>
  );
}
